'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
// TinyMCE React wrapper - assets loaded from CDN (media.tracverse.com)
import { Editor } from '@tinymce/tinymce-react';

// TinyMCE CDN base URL (self-hosted on our S3/CloudFront)
const TINYMCE_CDN_URL = 'https://media.tracverse.com/shared/tinymce';
import { Navbar } from '../../components/layout/navbar';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../components/ui/form';
import { toast } from 'sonner';
import { useForum } from '../../hooks/use-forum';
import { useCommunities } from '../../hooks/use-communities';
import {
  ChevronLeft,
  Eye,
  Edit3,
  Send,
  X,
  Sparkles,
  MessageSquare,
  Trash2,
  ImagePlus,
  Loader2,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import axios from '../../utils/axios-config';
import { useAuthStore } from '../../stores';
import { useGetUserById } from '../../hooks/use-user';
import { ProcessingSpinner } from '../../components/feedback/processing-spinner';
import { motion, AnimatePresence } from 'framer-motion';
import { TagSelector } from '../../components/forum/tag-selector';
import { TagBadgeList } from '../../components/ui/tag-badge';

// Validation schema
const formSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().max(200, 'Description must be 200 characters or less').optional(),
  coverImage: z.string().optional(),
  htmlContent: z.string().min(1, 'Content is required'),
  categoryId: z.string().min(1, 'Category is required'),
  tags: z.array(z.string()).max(5, 'Maximum 5 tags allowed').optional(),
});

// Form draft storage key prefix
const DRAFT_STORAGE_KEY = 'talktrail_discussion_draft';

// Helper to get storage key for a specific community
const getDraftStorageKey = (communitySlug) =>
  `${DRAFT_STORAGE_KEY}_${communitySlug || 'global'}`;

// Helper to safely parse JSON from localStorage
const getSavedDraft = (communitySlug) => {
  try {
    const saved = localStorage.getItem(getDraftStorageKey(communitySlug));
    if (saved) {
      const parsed = JSON.parse(saved);
      // Validate the saved data has expected structure
      if (parsed && typeof parsed === 'object') {
        return {
          title: parsed.title || '',
          description: parsed.description || '',
          coverImage: parsed.coverImage || '',
          htmlContent: parsed.htmlContent || '',
          categoryId: parsed.categoryId || '',
          tags: Array.isArray(parsed.tags) ? parsed.tags : [],
        };
      }
    }
  } catch (e) {
    console.warn('Failed to parse saved draft:', e);
  }
  return null;
};

// Helper to save draft to localStorage
const saveDraft = (communitySlug, data) => {
  try {
    localStorage.setItem(getDraftStorageKey(communitySlug), JSON.stringify(data));
  } catch (e) {
    console.warn('Failed to save draft:', e);
  }
};

// Helper to clear draft from localStorage
const clearDraft = (communitySlug) => {
  try {
    localStorage.removeItem(getDraftStorageKey(communitySlug));
  } catch (e) {
    console.warn('Failed to clear draft:', e);
  }
};

export default function CreateDiscussionPage() {
  const navigate = useNavigate();
  const { communitySlug } = useParams();
  const { walletAddress, isAuthenticated } = useAuthStore();

  // Get community data
  const { useGetCommunityBySlug, useCheckMembership } = useCommunities();
  const { data: community, isLoading: isLoadingCommunity } =
    useGetCommunityBySlug(communitySlug);

  // Get real user data from API
  const { data: user, isLoading: isLoadingUser } = useGetUserById(
    walletAddress
  );

  // Check community membership
  const { data: membershipData, isLoading: isLoadingMembership } =
    useCheckMembership(community?.oid, user?.oid);
  const canPost = membershipData?.canPost || false;

  // Redirect non-members back to the community page
  useEffect(() => {
    if (
      !isLoadingCommunity &&
      !isLoadingUser &&
      !isLoadingMembership &&
      community?.oid &&
      user?.oid
    ) {
      if (!canPost) {
        toast.error('You must join this community to create discussions');
        navigate(`/forum/${communitySlug}`);
      }
    }
  }, [
    isLoadingCommunity,
    isLoadingUser,
    isLoadingMembership,
    community?.oid,
    user?.oid,
    canPost,
    communitySlug,
    navigate,
  ]);

  const { createDiscussion } = useForum();

  // Use community's custom discussion categories instead of global categories
  const categories = community?.discussionCategories || [];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [spinnerStatus, setSpinnerStatus] = useState('processing'); // 'processing' | 'success' | 'error'
  const [spinnerError, setSpinnerError] = useState(null);
  const [createdDiscussionOid, setCreatedDiscussionOid] = useState(null);
  const [isUploadingCover, setIsUploadingCover] = useState(false);

  const editorRef = useRef(null);
  const coverInputRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const editorContentRef = useRef(''); // Store editor content without triggering re-renders
  const editorChangeTimeoutRef = useRef(null); // Debounce editor changes
  const [draftLoaded, setDraftLoaded] = useState(false);

  // Form setup - load saved draft as default values
  const savedDraft = getSavedDraft(communitySlug);
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: savedDraft || {
      title: '',
      description: '',
      coverImage: '',
      htmlContent: '',
      categoryId: '',
      tags: [],
    },
  });

  // Watch form values for preview and auto-save
  const { title, description, coverImage, htmlContent, categoryId, tags } = form.watch();

  // Load saved draft into TinyMCE editor on mount
  useEffect(() => {
    if (savedDraft && !draftLoaded) {
      setDraftLoaded(true);
      // If there's saved content, show a toast to let user know
      if (savedDraft.title || savedDraft.htmlContent) {
        toast.info('Draft restored from your last session');
      }
    }
  }, [draftLoaded]);

  // Auto-save form to localStorage (debounced)
  useEffect(() => {
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Don't save if form is empty or if we're submitting
    if (!title && !description && !coverImage && !htmlContent && !categoryId && (!tags || tags.length === 0)) {
      return;
    }

    // Debounce save - wait 1 second after last change
    saveTimeoutRef.current = setTimeout(() => {
      saveDraft(communitySlug, { title, description, coverImage, htmlContent, categoryId, tags });
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [title, description, coverImage, htmlContent, categoryId, tags, communitySlug]);

  // Focus title input on mount
  useEffect(() => {
    if (form.setFocus) {
      form.setFocus('title');
    }
  }, []);

  // Cleanup editor and timeouts on unmount (prevents memory leaks with Vite HMR)
  useEffect(() => {
    return () => {
      if (editorChangeTimeoutRef.current) {
        clearTimeout(editorChangeTimeoutRef.current);
      }
      // Destroy TinyMCE instance to prevent memory leaks
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
    };
  }, []);


  // Handle cover image upload
  const handleCoverImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB for cover images)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Cover image size should not exceed 5MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setIsUploadingCover(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/omniverse/upload/discussion-banner`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success && response.data.url) {
        form.setValue('coverImage', response.data.url);
        toast.success('Cover image uploaded successfully');
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Cover image upload error:', error);
      toast.error(error.message || 'Failed to upload cover image');
    } finally {
      setIsUploadingCover(false);
      // Reset the input so the same file can be selected again
      if (coverInputRef.current) {
        coverInputRef.current.value = '';
      }
    }
  };

  // Remove cover image
  const handleRemoveCoverImage = () => {
    form.setValue('coverImage', '');
  };

  // Form submit
  const onSubmit = async (data) => {
    // Validate wallet is connected
    if (!walletAddress) {
      toast.error('Please connect your wallet to create a discussion');
      return;
    }

    // Clear any pending editor debounce and use the latest content from ref
    if (editorChangeTimeoutRef.current) {
      clearTimeout(editorChangeTimeoutRef.current);
    }
    // Use the ref value which always has the latest content (debounce may not have fired yet)
    const htmlContent = editorContentRef.current || data.htmlContent;

    setIsSubmitting(true);
    setSpinnerStatus('processing');
    setSpinnerError(null);
    try {
      // Find the selected category - handle ObjectId comparison by converting to strings
      const selectedCat = categories.find((cat) => {
        const catId = (cat.id || cat._id || '').toString();
        const catName = cat.name || '';
        const searchId = (data.categoryId || '').toString();
        return catId === searchId || catName === searchId;
      });

      // Validate category was found
      if (!selectedCat) {
        toast.error('Please select a valid category');
        setIsSubmitting(false);
        return;
      }

      // Build discussion object - explicitly set category fields, don't spread selectedCat
      // to avoid adding unwanted fields like _id, createdAt, etc.
      let newDiscussion = {
        title: data.title,
        description: data.description || '', // Short description for previews
        coverImage: data.coverImage || '', // Cover/banner image URL
        htmlContent: htmlContent, // Use ref value for latest content
        category: selectedCat.name, // Category name for display
        categoryId: (selectedCat.id || selectedCat._id || selectedCat.name).toString(), // Consistent string ID
        tags: data.tags || [], // Include tags in the discussion
        communityOid: community?.oid || null, // Link discussion to community
        communitySlug: communitySlug || null,
        creator: walletAddress,
      };
      // Wrap in discussion object and include creator at top level for auth middleware
      newDiscussion = {
        discussion: { ...newDiscussion },
        creator: walletAddress, // Required by verifyIsCreator middleware
      };
      // Use useForum's createDiscussion mutation
      const createDiscussionMutation = createDiscussion();
      const result = await createDiscussionMutation.mutateAsync(newDiscussion);

      // Store the discussion OID for navigation
      if (result?.discussionOid) {
        setCreatedDiscussionOid(result.discussionOid);
      }

      // Clear saved draft on successful submission
      clearDraft(communitySlug);

      toast.success('Discussion posted!');
      setSpinnerStatus('success');
    } catch (error) {
      // Extract error message from various possible locations
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'There was an error posting your discussion. Please try again.';
      toast.error(errorMessage);
      setSpinnerStatus('error');
      setSpinnerError(errorMessage);
      console.error(
        'Discussion creation error:',
        error?.response?.data || error
      );
    } finally {
      // Do not setIsSubmitting(false) here; let the spinner control it
    }
  };

  // Handle clearing the form - must be defined before any conditional returns (Rules of Hooks)
  const handleClearForm = useCallback(() => {
    // Clear any pending editor debounce
    if (editorChangeTimeoutRef.current) {
      clearTimeout(editorChangeTimeoutRef.current);
    }
    // Reset form to empty values
    form.reset({
      title: '',
      description: '',
      coverImage: '',
      htmlContent: '',
      categoryId: '',
      tags: [],
    });
    // Clear the editor content and ref
    editorContentRef.current = '';
    if (editorRef.current) {
      editorRef.current.setContent('');
    }
    // Clear saved draft from localStorage
    clearDraft(communitySlug);
    toast.success('Form cleared');
  }, [form, communitySlug]);

  // Check if form has any content (to show/enable clear button)
  const hasFormContent = title || description || coverImage || htmlContent || categoryId || (tags && tags.length > 0);

  const selectedCategory = categories.find((c) => c.id === categoryId);

  // Show loading state while fetching user data
  if (isLoadingUser || isLoadingCommunity) {
    return (
      <div className="flex min-h-screen flex-col bg-black text-white">
        {/* Gradient Background */}
        <div className="fixed inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/20 blur-[120px]" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-600/20 blur-[120px]" />
        </div>
        <div className="fixed inset-0 z-0 pointer-events-none bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]" />

        <Navbar />
        <main className="flex-1 flex items-center justify-center relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 px-6"
          >
            <div className="w-16 h-16 rounded-full bg-violet-500/20 flex items-center justify-center mx-auto mb-6">
              <div className="w-8 h-8 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
            </div>
            <p className="text-zinc-400">Loading...</p>
          </motion.div>
        </main>
      </div>
    );
  }

  // Only show sign-in prompt if not loading AND no user (actually not authenticated)
  if (!user && !walletAddress) {
    return (
      <div className="flex min-h-screen flex-col bg-black text-white">
        {/* Gradient Background */}
        <div className="fixed inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/20 blur-[120px]" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-600/20 blur-[120px]" />
        </div>
        <div className="fixed inset-0 z-0 pointer-events-none bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]" />

        <Navbar />
        <main className="flex-1 flex items-center justify-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12 px-6"
          >
            <div className="w-16 h-16 rounded-full bg-violet-500/20 flex items-center justify-center mx-auto mb-6">
              <MessageSquare className="w-8 h-8 text-violet-400" />
            </div>
            <h1 className="text-2xl font-bold mb-3 text-white">
              Sign in to start a discussion
            </h1>
            <p className="text-zinc-400 mb-6 max-w-md">
              Connect your wallet to join the conversation and share your
              thoughts with the community.
            </p>
            <Button
              onClick={() => navigate('/sign-in')}
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              Sign In
            </Button>
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-black text-white">
      {/* Gradient Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/20 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-600/20 blur-[120px]" />
      </div>
      <div className="fixed inset-0 z-0 pointer-events-none bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]" />

      <ProcessingSpinner
        isOpen={isSubmitting}
        status={spinnerStatus}
        error={spinnerError}
        onClose={() => {
          setIsSubmitting(false);
          setSpinnerStatus('processing');
          setSpinnerError(null);
        }}
        onRetry={() => {
          setSpinnerStatus('processing');
          setSpinnerError(null);
          form.handleSubmit(onSubmit)();
        }}
        title="Posting Discussion"
        processingMessage="Your discussion is being posted. Please wait..."
        successMessage="Your discussion has been posted!"
        errorMessage="There was an error posting your discussion."
        successActions={
          spinnerStatus === 'success' && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  // Reset form and state for new discussion
                  form.reset({
                    title: '',
                    description: '',
                    coverImage: '',
                    htmlContent: '',
                    categoryId: '',
                    tags: [],
                  });
                  setCreatedDiscussionOid(null);
                  setIsSubmitting(false);
                  setSpinnerStatus('processing');
                  // Clear editor content
                  if (editorRef.current) {
                    editorRef.current.setContent('');
                  }
                }}
                className="border-white/10 text-zinc-300 hover:text-white hover:bg-white/5"
              >
                Create Another
              </Button>
              <Button
                onClick={() => {
                  setIsSubmitting(false);
                  setSpinnerStatus('processing');
                  if (createdDiscussionOid) {
                    const basePath = communitySlug
                      ? `/forum/${communitySlug}`
                      : '/forum';
                    navigate(`${basePath}/discussion/${createdDiscussionOid}`);
                  }
                }}
                className="bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white"
              >
                View Discussion
              </Button>
            </>
          )
        }
      />

      {/* Navbar */}
      <div className="sticky top-0 z-40 w-full bg-black/60 backdrop-blur-xl border-b border-white/5">
        <Navbar />
      </div>

      {/* Main Content */}
      <main className="flex-1 relative z-10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Link
                to="/forum"
                className="flex items-center justify-center w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
              >
                <ChevronLeft className="h-5 w-5 text-zinc-400" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-white">New Discussion</h1>
                <p className="text-sm text-zinc-500">
                  Share your thoughts with the community
                </p>
              </div>
            </div>

            {/* Mode Toggle */}
            <div className="flex items-center gap-2 bg-white/5 rounded-full p-1">
              <button
                onClick={() => setPreviewMode(false)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${!previewMode
                    ? 'bg-violet-600 text-white shadow-lg'
                    : 'text-zinc-400 hover:text-white'
                  }`}
              >
                <Edit3 className="w-4 h-4" />
                <span className="hidden sm:inline">Edit</span>
              </button>
              <button
                onClick={() => setPreviewMode(true)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${previewMode
                    ? 'bg-violet-600 text-white shadow-lg'
                    : 'text-zinc-400 hover:text-white'
                  }`}
              >
                <Eye className="w-4 h-4" />
                <span className="hidden sm:inline">Preview</span>
              </button>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {previewMode ? (
              /* Preview Mode */
              <motion.div
                key="preview"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="max-w-4xl mx-auto"
              >
                <div className="bg-zinc-900/50 border border-white/10 rounded-2xl overflow-hidden">
                  {/* Preview Header with Cover Image or Category Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={coverImage || selectedCategory?.image || '/placeholder-cover.jpg'}
                      alt={title || 'Cover'}
                      className="w-full h-full object-cover opacity-60"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/50 to-transparent" />
                    <div className="absolute bottom-4 left-6 flex items-center gap-2 flex-wrap">
                      {selectedCategory && (
                        <Badge
                          className="text-white border-0"
                          style={{ backgroundColor: selectedCategory.color }}
                        >
                          {selectedCategory.name}
                        </Badge>
                      )}
                      {tags && tags.length > 0 && (
                        <TagBadgeList tags={tags} size="sm" maxVisible={5} />
                      )}
                    </div>
                  </div>

                  <div className="p-6 md:p-8">
                    <h1 className="text-3xl font-bold mb-3 text-white">
                      {title || 'Untitled Discussion'}
                    </h1>
                    {description && (
                      <p className="text-zinc-400 text-lg mb-6 border-l-2 border-violet-500/50 pl-4">
                        {description}
                      </p>
                    )}
                    <div className="prose prose-lg prose-invert max-w-none">
                      {htmlContent ? (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(htmlContent),
                          }}
                        />
                      ) : (
                        <p className="text-zinc-500 italic">
                          No content yet. Switch to edit mode to start writing.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons for Preview */}
                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setPreviewMode(false)}
                    className="border-white/10 text-zinc-300 hover:text-white hover:bg-white/5"
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Continue Editing
                  </Button>
                  <Button
                    onClick={form.handleSubmit(onSubmit)}
                    disabled={
                      isSubmitting || !title || !htmlContent || !categoryId
                    }
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Post Discussion
                  </Button>
                </div>
              </motion.div>
            ) : (
              /* Edit Mode */
              <motion.div
                key="edit"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
              >
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Main Editor Column */}
                      <div className="lg:col-span-2 space-y-6">
                        {/* Title & Description */}
                        <div className="bg-zinc-900/50 border border-white/10 rounded-2xl p-6 space-y-4">
                          <FormField
                            control={form.control}
                            name="title"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-sm font-medium text-zinc-400 mb-2 block">
                                  Discussion Title
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    placeholder="What do you want to discuss?"
                                    className="text-xl font-semibold bg-transparent border-0 border-b border-white/10 rounded-none px-0 py-3 text-white placeholder:text-zinc-600 focus:border-violet-500 focus:ring-0 focus-visible:ring-0"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="description"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-sm font-medium text-zinc-400 mb-2 block">
                                  Short Description{' '}
                                  <span className="text-zinc-600">(optional, max 200 characters)</span>
                                </FormLabel>
                                <FormControl>
                                  <Textarea
                                    {...field}
                                    placeholder="A brief summary of your discussion that appears in previews..."
                                    maxLength={200}
                                    rows={2}
                                    className="bg-zinc-800/50 border-white/10 text-white placeholder:text-zinc-600 focus:border-violet-500 resize-none"
                                  />
                                </FormControl>
                                <div className="flex justify-between items-center mt-1">
                                  <FormMessage />
                                  <span className="text-xs text-zinc-500">
                                    {(field.value || '').length}/200
                                  </span>
                                </div>
                              </FormItem>
                            )}
                          />
                        </div>

                        {/* Cover Image */}
                        <div className="bg-zinc-900/50 border border-white/10 rounded-2xl p-6">
                          <FormField
                            control={form.control}
                            name="coverImage"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-sm font-medium text-zinc-400 mb-4 block">
                                  Cover Image{' '}
                                  <span className="text-zinc-600">(optional)</span>
                                </FormLabel>
                                <FormControl>
                                  <div className="space-y-4">
                                    {field.value ? (
                                      <div className="relative group">
                                        <img
                                          src={field.value}
                                          alt="Cover preview"
                                          className="w-full h-48 object-cover rounded-xl border border-white/10"
                                        />
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                                          <Button
                                            type="button"
                                            variant="destructive"
                                            size="sm"
                                            onClick={handleRemoveCoverImage}
                                            className="bg-red-600 hover:bg-red-700"
                                          >
                                            <X className="w-4 h-4 mr-1" />
                                            Remove
                                          </Button>
                                        </div>
                                      </div>
                                    ) : (
                                      <div
                                        onClick={() => coverInputRef.current?.click()}
                                        className="border-2 border-dashed border-white/10 hover:border-violet-500/50 rounded-xl p-8 cursor-pointer transition-colors flex flex-col items-center justify-center gap-3 group"
                                      >
                                        {isUploadingCover ? (
                                          <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
                                        ) : (
                                          <ImagePlus className="w-8 h-8 text-zinc-500 group-hover:text-violet-400 transition-colors" />
                                        )}
                                        <div className="text-center">
                                          <p className="text-sm text-zinc-400 group-hover:text-zinc-300">
                                            {isUploadingCover ? 'Uploading...' : 'Click to upload cover image'}
                                          </p>
                                          <p className="text-xs text-zinc-600">
                                            PNG, JPG, GIF up to 5MB
                                          </p>
                                        </div>
                                      </div>
                                    )}
                                    <input
                                      ref={coverInputRef}
                                      type="file"
                                      accept="image/*"
                                      onChange={handleCoverImageUpload}
                                      className="hidden"
                                    />
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        {/* Content Editor */}
                        <div className="bg-zinc-900/50 border border-white/10 rounded-2xl p-6">
                          <FormField
                            control={form.control}
                            name="htmlContent"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-sm font-medium text-zinc-400 mb-4 block">
                                  Content
                                </FormLabel>
                                <FormControl>
                                  <div className="tinymce-dark-wrapper rounded-xl overflow-hidden">
                                    <Editor
                                      tinymceScriptSrc={`${TINYMCE_CDN_URL}/tinymce.min.js`}
                                      onInit={(evt, editor) => {
                                        editorRef.current = editor;
                                        // Initialize ref with current value from saved draft
                                        editorContentRef.current = field.value || '';
                                        // Restore content from form when editor initializes
                                        if (field.value) {
                                          editor.setContent(field.value);
                                        }
                                      }}
                                      initialValue={field.value || ''}
                                      onEditorChange={(content) => {
                                        // Store in ref immediately (no re-render, no form state change)
                                        // This prevents cursor reset by avoiding React re-renders
                                        editorContentRef.current = content;
                                        // Auto-save draft to localStorage (debounced)
                                        if (editorChangeTimeoutRef.current) {
                                          clearTimeout(editorChangeTimeoutRef.current);
                                        }
                                        editorChangeTimeoutRef.current = setTimeout(() => {
                                          // Save to draft storage, but don't trigger form onChange
                                          // Form will get the value from ref on submit/blur
                                          const currentValues = form.getValues();
                                          saveDraft(communitySlug, {
                                            ...currentValues,
                                            htmlContent: content,
                                          });
                                        }, 500);
                                      }}
                                      onBlur={() => {
                                        // Sync ref to form on blur (when user clicks away)
                                        // This ensures form validation works
                                        field.onChange(editorContentRef.current);
                                      }}
                                      init={{
                                        license_key: 'gpl', // Required for TinyMCE 7+ open source usage
                                        menubar: false,
                                        // CDN-hosted TinyMCE paths (media.tracverse.com)
                                        skin_url: `${TINYMCE_CDN_URL}/skins/ui/oxide-dark`,
                                        content_css: `${TINYMCE_CDN_URL}/skins/content/dark/content.min.css`,
                                        height: 400,
                                        plugins:
                                          'autolink charmap codesample emoticons image link lists media searchreplace table visualblocks wordcount code image autosave autoresize',
                                        toolbar:
                                          'bold italic underline | link image media | bullist numlist | code',
                                        branding: false,
                                        promotion: false,
                                        image_caption: true,
                                        content_style: `
                                          body { background-color: #18181b; color: #fff; font-family: Inter, sans-serif; padding: 16px; }
                                          img { max-width: 100%; height: auto; margin: 10px 0; border-radius: 8px; }
                                        `,
                                        file_picker_types: 'image',
                                        automatic_uploads: true,
                                        images_upload_url: `${import.meta.env.VITE_API_BASE_URL
                                          }/omniverse/upload/discussion-image`,
                                        images_upload_handler: async (
                                          blobInfo,
                                          progress
                                        ) => {
                                          try {
                                            const file = blobInfo.blob();
                                            if (file.size > 2 * 1024 * 1024) {
                                              throw new Error(
                                                'Image size should not exceed 2MB'
                                              );
                                            }
                                            const formData = new FormData();
                                            formData.append(
                                              'file',
                                              file,
                                              blobInfo.filename()
                                            );
                                            const response = await axios.post(
                                              `${import.meta.env
                                                .VITE_API_BASE_URL
                                              }/omniverse/upload/discussion-image`,
                                              formData,
                                              {
                                                headers: {
                                                  'Content-Type':
                                                    'multipart/form-data',
                                                },
                                                onUploadProgress: (
                                                  progressEvent
                                                ) => {
                                                  progress(
                                                    (progressEvent.loaded /
                                                      progressEvent.total) *
                                                    100
                                                  );
                                                },
                                              }
                                            );
                                            if (
                                              response.data.success &&
                                              response.data.url
                                            ) {
                                              return response.data.url;
                                            } else {
                                              throw new Error('Upload failed');
                                            }
                                          } catch (error) {
                                            if (editorRef.current) {
                                              editorRef.current.notificationManager.open(
                                                {
                                                  text:
                                                    error.message ||
                                                    'Image upload failed',
                                                  type: 'error',
                                                  timeout: 3000,
                                                }
                                              );
                                            }
                                            throw error;
                                          }
                                        },
                                        file_picker_callback: (
                                          cb,
                                          value,
                                          meta
                                        ) => {
                                          const input =
                                            document.createElement('input');
                                          input.setAttribute('type', 'file');
                                          input.setAttribute(
                                            'accept',
                                            'image/*'
                                          );
                                          input.addEventListener(
                                            'change',
                                            (e) => {
                                              const file = e.target.files[0];
                                              if (file.size > 2 * 1024 * 1024) {
                                                if (editorRef.current) {
                                                  editorRef.current.notificationManager.open(
                                                    {
                                                      text: 'Image size should not exceed 2MB',
                                                      type: 'error',
                                                      timeout: 3000,
                                                    }
                                                  );
                                                }
                                                return;
                                              }
                                              const reader = new FileReader();
                                              reader.addEventListener(
                                                'load',
                                                () => {
                                                  const id =
                                                    'blobid' +
                                                    new Date().getTime();
                                                  const blobCache =
                                                    window.tinymce.activeEditor
                                                      .editorUpload.blobCache;
                                                  const base64 =
                                                    reader.result.split(',')[1];
                                                  const blobInfo =
                                                    blobCache.create(
                                                      id,
                                                      file,
                                                      base64
                                                    );
                                                  blobCache.add(blobInfo);
                                                  cb(blobInfo.blobUri(), {
                                                    title: file.name,
                                                  });
                                                }
                                              );
                                              reader.readAsDataURL(file);
                                            }
                                          );
                                          input.click();
                                        },
                                      }}
                                    />
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>

                      {/* Sidebar Column */}
                      <div className="space-y-6">
                        {/* Category Selection */}
                        <div className="bg-zinc-900/50 border border-white/10 rounded-2xl p-6">
                          <FormField
                            control={form.control}
                            name="categoryId"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-sm font-medium text-zinc-400 mb-4 block">
                                  Select Category
                                </FormLabel>
                                <FormControl>
                                  <div className="space-y-2 max-h-[300px] overflow-y-auto custom-scrollbar pr-2">
                                    {categories.map((category, index) => {
                                      const categoryId =
                                        category.id ||
                                        category._id ||
                                        category.name;
                                      return (
                                        <button
                                          key={categoryId || index}
                                          type="button"
                                          onClick={() =>
                                            field.onChange(categoryId)
                                          }
                                          className={`w-full text-left p-3 rounded-xl transition-all flex items-start gap-3 ${field.value === categoryId
                                              ? 'bg-violet-600/20 border border-violet-500/50'
                                              : 'bg-white/5 border border-transparent hover:bg-white/10'
                                            }`}
                                        >
                                          <div
                                            className="w-3 h-3 rounded-full flex-shrink-0 mt-1"
                                            style={{
                                              backgroundColor:
                                                category.color || '#8B5CF6',
                                            }}
                                          />
                                          <div className="flex-1">
                                            <span
                                              className={`text-sm font-medium block ${field.value === categoryId
                                                  ? 'text-white'
                                                  : 'text-zinc-300'
                                                }`}
                                            >
                                              {category.name}
                                            </span>
                                            {category.description && (
                                              <span className="text-xs text-zinc-500 block mt-1">
                                                {category.description}
                                              </span>
                                            )}
                                          </div>
                                        </button>
                                      );
                                    })}
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        {/* Tag Selection */}
                        <div className="bg-zinc-900/50 border border-white/10 rounded-2xl p-6">
                          <FormField
                            control={form.control}
                            name="tags"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-sm font-medium text-zinc-400 mb-4 block">
                                  Add Tags (optional)
                                </FormLabel>
                                <FormControl>
                                  <TagSelector
                                    value={field.value || []}
                                    onChange={field.onChange}
                                    maxTags={5}
                                    placeholder="Select tags..."
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        {/* Tips Card */}
                        <div className="bg-gradient-to-br from-violet-600/20 to-cyan-600/20 border border-white/10 rounded-2xl p-6">
                          <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-4 h-4 text-violet-400" />
                            <span className="text-sm font-medium text-white">
                              Writing Tips
                            </span>
                          </div>
                          <ul className="space-y-2 text-sm text-zinc-400">
                            <li>• Use a clear, descriptive title</li>
                            <li>• Break up long text with headings</li>
                            <li>• Add images to illustrate points</li>
                            <li>• Be respectful and constructive</li>
                          </ul>
                        </div>

                        {/* Action Buttons */}
                        <div className="bg-zinc-900/50 border border-white/10 rounded-2xl p-6 space-y-3">
                          <Button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full bg-violet-600 hover:bg-violet-700 text-white h-12"
                          >
                            <Send className="w-4 h-4 mr-2" />
                            {isSubmitting ? 'Posting...' : 'Post Discussion'}
                          </Button>

                          {/* Clear Form Button with Confirmation */}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                disabled={!hasFormContent}
                                className="w-full border-white/10 text-zinc-300 hover:text-red-400 hover:border-red-500/50 hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Clear Form
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-zinc-900 border-white/10">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-white">Clear form?</AlertDialogTitle>
                                <AlertDialogDescription className="text-zinc-400">
                                  This will remove all content from the form including your title, content, selected category, and tags. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10 hover:text-white">
                                  Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={handleClearForm}
                                  className="bg-red-600 hover:bg-red-700 text-white"
                                >
                                  Clear Form
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>

                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate(-1)}
                            className="w-full border-white/10 text-zinc-300 hover:text-white hover:bg-white/5"
                          >
                            <X className="w-4 h-4 mr-2" />
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  </form>
                </Form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
